import { useQuery } from '@tanstack/react-query'
import { http } from '../../utils/httpCommon'
import { apiUrls } from '../../utils/apiUrls'
import { severityPercentageApiResponseSchema, SeverityPercentageApiResponse } from '../../types/sentryIssue.type'
import {
  isDispatchMobileProject,
  isDockAutomationProject,
  SENTRY_PROJECT_APEX_CLIENT,
} from '../../constants/sentryProjects'
import { getWorkstreamCulpritQuery } from '../../utils/workstreamHelper'
import { sentryQueryKeys } from './queryKeys'

interface UseSeverityPercentageParams {
  startDate: string // ISO format: YYYY-MM-DDTHH:mm:ss
  endDate: string // ISO format: YYYY-MM-DDTHH:mm:ss
  project?: string
  workstream?: string | null
  environment?: string | null
  /** For Dispatch Mobile/Dock Automation: optional title search (query=title:"...") */
  searchQuery?: string
}

/**
 * Custom hook to fetch severity percentage changes from the Sentry API
 * This hook fetches comparison data showing how issue counts have changed
 * compared to the previous period
 *
 * @param params - Query parameters including date range, project, and workstream
 * @returns React Query result with severity percentage data
 */
export const useSeverityPercentage = ({
  startDate,
  endDate,
  project = SENTRY_PROJECT_APEX_CLIENT,
  workstream = null,
  environment = null,
  searchQuery,
}: UseSeverityPercentageParams) => {
  const searchQueryNorm = searchQuery?.trim() || null
  return useQuery({
    queryKey: sentryQueryKeys.severityPercentage({
      projectId: project,
      startDate,
      endDate,
      workstream,
      environment,
      searchQuery: searchQueryNorm,
    }),
    enabled: !!startDate && !!endDate, // Only run query when dates are available
    queryFn: async (): Promise<SeverityPercentageApiResponse> => {
      // Build query params
      const params = new URLSearchParams()

      // Parse the date strings to check if it's the default 24-hour range (yesterday to today)
      const startDateObj = new Date(startDate)
      const endDateObj = new Date(endDate)

      // Check if it's exactly yesterday to today (default range)
      // Only use statsPeriod=24h for the default range, not for single date selections
      const isSameDate =
        startDateObj.getFullYear() === endDateObj.getFullYear() &&
        startDateObj.getMonth() === endDateObj.getMonth() &&
        startDateObj.getDate() === endDateObj.getDate()

      const is24HoursRange =
        !isSameDate && Math.abs(endDateObj.getTime() - startDateObj.getTime()) <= 24 * 60 * 60 * 1000

      // Add statsPeriod only for yesterday-to-today range, otherwise use explicit start/end dates
      if (is24HoursRange) {
        params.append('statsPeriod', '24h')
      } else {
        // Format dates: start at 00:00:00, end at 23:59:59
        const startYear = startDateObj.getFullYear()
        const startMonth = String(startDateObj.getMonth() + 1).padStart(2, '0')
        const startDay = String(startDateObj.getDate()).padStart(2, '0')
        const formattedStart = `${startYear}-${startMonth}-${startDay}T00:00:00`
        params.append('start', formattedStart)

        const endYear = endDateObj.getFullYear()
        const endMonth = String(endDateObj.getMonth() + 1).padStart(2, '0')
        const endDay = String(endDateObj.getDate()).padStart(2, '0')
        const formattedEnd = `${endYear}-${endMonth}-${endDay}T23:59:59`
        params.append('end', formattedEnd)
      }

      params.append('project', project)

      if (environment && environment !== 'all') {
        params.append('environment', environment)
      }

      if (isDispatchMobileProject(project) || isDockAutomationProject(project)) {
        // Always send query parameter for Dispatch Mobile and Dock Automation
        // If searchQuery is provided, use title search; otherwise send empty query
        if (searchQueryNorm) {
          params.append('query', `title:"${searchQueryNorm}"`)
        } else {
          params.append('query', '')
        }
      } else {
        // Apex Client logic
        if (workstream !== null && workstream !== 'all') {
          // Workstream filter provided (e.g., from SentryDashboard)
          const culpritQuery = getWorkstreamCulpritQuery(workstream)
          if (culpritQuery) {
            params.append('query', culpritQuery)
          }
        } else {
          // No workstream filter (e.g., Overview page) - send empty query
          params.append('query', '')
        }
      }

      const apiEndpoint = `${apiUrls.getSeverityPercentage}?${params.toString()}`
      const response = await http.get(apiEndpoint)
      const raw = response.data as Record<string, unknown> | null | undefined

      // Resolve the payload: top-level or under "data" (some APIs wrap the body)
      const payload =
        raw &&
        typeof raw === 'object' &&
        'Severity-counts-Comparison' in raw &&
        raw['Severity-counts-Comparison'] != null
          ? raw
          : raw && typeof raw === 'object' && 'data' in raw && raw.data != null && typeof raw.data === 'object'
            ? (raw.data as Record<string, unknown>)
            : raw

      // Validate and coerce with Zod (success is optional; numbers may come as strings)
      const parseResult = severityPercentageApiResponseSchema.safeParse(payload)

      if (parseResult.success) {
        return parseResult.data
      }

      // Fallback: extract "Severity-counts-Comparison" from raw so UI still gets values
      const comparison =
        (raw && typeof raw === 'object' && raw['Severity-counts-Comparison']) ||
        (raw &&
          typeof raw === 'object' &&
          raw.data != null &&
          typeof raw.data === 'object' &&
          (raw.data as Record<string, unknown>)['Severity-counts-Comparison'])

      const comparisonObj =
        comparison != null && typeof comparison === 'object' ? (comparison as Record<string, unknown>) : undefined

      const coerceNum = (v: unknown): number =>
        v !== undefined && v !== null && !Number.isNaN(Number(v)) ? Number(v) : 0

      const entry = (key: string) => {
        const e = comparisonObj?.[key]
        if (e == null || typeof e !== 'object') return { count: 0, previousCount: 0, change: 0, percentageChange: 0 }
        const o = e as Record<string, unknown>
        return {
          count: coerceNum(o.count),
          previousCount: coerceNum(o.previousCount),
          change: coerceNum(o.change),
          percentageChange: coerceNum(o.percentageChange),
        }
      }

      return {
        success: true,
        'Severity-counts-Comparison': {
          high: entry('high'),
          medium: entry('medium'),
          low: entry('low'),
          total: entry('total'),
          comparisonPeriod:
            typeof comparisonObj?.comparisonPeriod === 'string' ? comparisonObj.comparisonPeriod : undefined,
        },
      } as SeverityPercentageApiResponse
    },
    // Refetch when window regains focus
    refetchOnWindowFocus: false,
    // Cache data for 5 minutes
    staleTime: 5 * 60 * 1000,
    // Keep data in cache for 10 minutes
    gcTime: 10 * 60 * 1000,
  })
}
